'use client';

import NextImage, { type ImageProps } from 'next/image';
import React, { useState } from 'react';

import { Skeleton } from '@ui/components/ui/skeleton';
import { cn } from '@ui/lib/utils';

import { DefaultFallback } from './DefaultFallback';

interface ImageWithFallbackProps {
  src?: string;
  alt: string;
  fallback?: React.ReactNode;
  className?: string;
  sizes?: string;
  /**
   * Priority images are preloaded by Next.js and skip the loading skeleton.
   * Use for LCP (Largest Contentful Paint) images.
   * @default false
   */
  priority?: boolean;
  /**
   * Forwarded to next/image's `quality` prop. Defaults to 60.
   */
  quality?: number;
}

/**
 * Wraps next/image with a skeleton-while-loading state and a fallback for
 * missing/broken images. Crucially, this defers entirely to next/image for
 * loading — we don't issue a parallel `new Image()` preload of the raw
 * source, which would double the bandwidth (full-size + optimized variant)
 * for every card on the page.
 */
export const ImageWithFallback: React.FC<ImageWithFallbackProps & Omit<ImageProps, 'src' | 'alt' | 'fill'>> = ({
  src,
  alt,
  fallback,
  className = '',
  sizes = '(max-width: 768px) 100vw, 50vw',
  priority = false,
  // q=60 by default — Lighthouse's "Improve image delivery" insight flagged
  // every activity-card thumbnail with q=75 as having ~50 % byte savings
  // available. The visual difference at thumbnail scale is imperceptible.
  // Callers that need higher fidelity can still pass an explicit `quality` prop.
  quality = 60,
  ...props
}) => {
  // Priority (LCP) images skip the loading state — next/image preloads them
  // and they should paint as soon as bytes arrive.
  const [isLoaded, setIsLoaded] = useState(priority);
  const [hasError, setHasError] = useState(false);

  if (!src || hasError) {
    return <>{fallback ?? <DefaultFallback className={className} />}</>;
  }

  return (
    <>
      {!isLoaded && <Skeleton className={className} />}
      <NextImage
        src={src}
        alt={alt}
        fill
        sizes={sizes}
        priority={priority}
        quality={quality}
        className={cn(className, !isLoaded && 'opacity-0')}
        onLoad={() => setIsLoaded(true)}
        onError={() => setHasError(true)}
        {...props}
      />
    </>
  );
};
